import React from 'react';
import { graphql, HeadProps, PageProps } from 'gatsby';
import Layout from '../../component/Layout';
import RecipesList from '../../component/RecipesList';
import { Common, SanityArticleCategory, SanityRecipesRelated } from '../../types/common';
import { urlFor } from '../../utils/imageBuilder';
import RichText from '../../component/RichText';
import NutritionTable from '../../component/NutritionTable';
import { DifficultIcon, ServingIcon, TimeIcon } from '../../component/Icons';
import ActionIcon from '../../component/Icons/Action';
import SocialShare from '../../component/SocialShare';
import Hero from '../../component/Hero';
import { useScreenSize } from '../../hooks/useScreenSize';
import { SEO } from '../../component/SEO';
import './styles.scss';

export const data = graphql`
  query($_id: String) {
    sanityRecipes(_id: { eq: $_id }) {
      ...RecipesAllFields
    }
    ...Common
  }
`;

type propsRecipes = {
  sanityRecipes: {
    _id: string;
    slug: {
      current: string;
    };
    seo: {
      metaDescription: string;
      metaTitle: string;
    };
    time?: string;
    activeTime?: string;
    _rawIngredients: any;
    _rawDirections: any;
    author: string;
    nutritionTable: {
      rows: {
        cells: string[];
      }[]
    };
    image: {
      alt: string;
      asset: {
        url: string;
        altText: string;
      }
    }
    serving: string;
    difficult: string;
    categories?: SanityArticleCategory[] | null;
    title: string;
    teaser: {
      cta: string;
      description: string;
      title: string;
      image: {
        alt: string;
        asset: {
          path: string
        }
      }
    }
    recipesRelated: SanityRecipesRelated;
  }
} & Common;

export function Head({ data }: HeadProps<propsRecipes>) {
  return (
    <>
      <SEO data={data.sanityRecipes} settings={data.sanitySiteSettings} />;
      <meta property="og:title" content={data.sanityRecipes.seo.metaTitle} />
      {data.sanityRecipes.seo.metaDescription && (
        <meta property="og:description" content={data.sanityRecipes.seo.metaDescription} />
      )}
      <meta property="og:type" content="article" />
      <meta property="og:image" content={urlFor(data.sanityRecipes.image).url()} />
    </>
  )
}

const Recipes = ({ data }: PageProps<propsRecipes>) => {
  const recipeData = data.sanityRecipes;
  const { isMobile } = useScreenSize();

  return (
    <Layout
      common={data}
    >
      <Hero
        title={recipeData.title}
        breadcrumb={recipeData}
      />
      <section className='recipe'>
        <div className='details'>
          {recipeData.image && (
            <img
              src={urlFor(recipeData.image).width(isMobile ? 375 : 650).quality(90).url()}
              alt={recipeData.image.alt}
            />
          )}
          <div className='recipe-infos'>
            {recipeData.author && <p className='text-small-bold'>{recipeData.author}</p>}
            <div className='group-icons'>
              {recipeData.time && (
                <div>
                  <TimeIcon />
                  <p className='text-small'>{recipeData.time}</p>
                </div>
              )}
              {recipeData.activeTime && (
                <div>
                  <ActionIcon />
                  <p className='text-small'>{recipeData.activeTime}</p>
                </div>
              )}
              {recipeData.serving && (
                <div>
                  <ServingIcon />
                  <p className='text-small'>{recipeData.serving}</p>
                </div>
              )}
              {recipeData.difficult && (
                <div>
                  <DifficultIcon />
                  <p className='text-small'>{recipeData.difficult}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {recipeData._rawIngredients && (
          <div className='ingredients'>
            <h3>Ingredients</h3>
            <RichText
              value={recipeData._rawIngredients}
            />
          </div>
        )}
        {recipeData._rawDirections && (
          <div className='directions'>
            <h2>Directions</h2>
            <RichText
              value={recipeData._rawDirections}
            />
          </div>
        )}

        {recipeData.nutritionTable?.rows && (
          <div className='nutrition'>
            <h2>Nutrients Per serving</h2>
            <NutritionTable rows={recipeData.nutritionTable.rows} />
          </div>
        )}

        <SocialShare
          message={`${recipeData.title}`}
        />
      </section>

      <RecipesList
        title='Related recipes'
        description='Fast and easy to do'
        recipes={recipeData.recipesRelated}
        categories={recipeData.categories}
        currentRecipeId={recipeData._id}
      />
    </Layout>
  );
};

export default Recipes;
